import { NgIf } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldAppearance, MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, takeUntil } from 'rxjs';
import { RecommendationType } from '../../../recommendation/dto/recommendation-type';
import { BoolDto } from '../../../../common/dto/bool-dto';
import { RecommendationPropertiesService } from '../../../recommendation/service/recommendation-properties.service';
import { StepRecomService } from '../../service/step-recom.service';
import { StateNotifyService } from '../../../../common/state/service/state-notify.service';
import { Router } from '@angular/router';
import { StepRecom } from '../../interface/step-recom';
import { ConfirmDialogComponent } from '../../../../common/component/dialog/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'dpi-step-recom-detail',
  imports: [FormsModule, MatFormFieldModule, MatInputModule, MatCardModule, MatIconModule, MatButtonModule, MatToolbarModule,
    MatTooltipModule, MatSelectModule, MatDialogModule, NgIf],
  templateUrl: './step-recom-detail.component.html',
  styleUrl: './step-recom-detail.component.scss'
})
export class StepRecomDetailComponent implements OnInit, OnDestroy {
  fieldAppearance: MatFormFieldAppearance;
  destroyed$ = new Subject<void>(); // notifier of component being destroyed
  isReadonly: boolean;
  isContentChanged: boolean;
  private _dialogConfirm = inject(MatDialog);

  stepRecom: StepRecom;

  recommedationTypes: RecommendationType[];
  selectedRecommendationType: RecommendationType;

  closedStates: BoolDto[];
  selectedCloseState: BoolDto;

  constructor(private stepRecomService: StepRecomService
    , private recommendationPropertiesService: RecommendationPropertiesService
    , private notifyService: StateNotifyService
    , private router: Router) {
    this.isReadonly = true;
    this.fieldAppearance = 'outline';
    this.isContentChanged = false;

    this.stepRecom = this.notifyService.getSelectedStepRecom();

    this.recommedationTypes = this.recommendationPropertiesService.getAllRecommendationTypes();
    this.selectedRecommendationType = this.recommendationPropertiesService.getRecommendationType(this.recommedationTypes, this.stepRecom.type);

    this.closedStates = this.recommendationPropertiesService.getAllRecommendationClosedStates();
    this.selectedCloseState = this.recommendationPropertiesService.getRecommendationClosedState(this.closedStates, this.stepRecom.closed);
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngOnInit(): void {
    this.notifyService.stepRecomSelection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(model => {
        this.stepRecom = model;
        this.selectedRecommendationType = this.recommendationPropertiesService.getRecommendationType(this.recommedationTypes, this.stepRecom.type);
        this.selectedCloseState = this.recommendationPropertiesService.getRecommendationClosedState(this.closedStates, this.stepRecom.closed);
      });

    this.notifyService.readonlyMode$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(res => {
        this.onEditModeChange(res);
      });
  }

  onEditModeChange(isReadonly: boolean) {
    this.isReadonly = isReadonly;
    if (isReadonly) {
      this.fieldAppearance = 'outline';
    } else {
      this.fieldAppearance = 'fill';
    }
  }

  onContentChange() {
    this.isContentChanged = true;
  }

  recommendationTypeChanged() {
    this.stepRecom.type = this.selectedRecommendationType.id;
    this.isContentChanged = true;
  }

  closeStateChanged() {
    this.stepRecom.closed = this.selectedCloseState.state;
    this.isContentChanged = true;
  }

  // --- CRUD actions ---

  createStepRecom() {
    this.stepRecomService.createStepRecom(this.stepRecom.test_step_id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: model => {
          this.addModelToSytem(model);
        },
        error: err => { console.log(err); }
      });
  }

  updateStepRecom() {
    this.isContentChanged = false; // Reset the state, and disable the button to prevent multiple clicks
    this.stepRecomService.updateStepRecom(this.stepRecom)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: model => {
          this.stepRecom = model;
          this.notifyService.updateStepRecomSelection(model);
        },
        error: err => { console.log(err); }
      });
  }

  deleteStepRecom() {
    this.stepRecomService.deleteStepRecom(this.stepRecom.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: res => {
          this.removeStepRecomAfterDelete();
        },
        error: err => { console.log(err); }
      });
  }

  private addModelToSytem(model: StepRecom) {
    this.stepRecom = model;
    this.notifyService.updateStepRecomSelection(this.stepRecom);
    let listStepRecom = this.notifyService.getListAllStepRecom();
    listStepRecom.push(model);
    this.notifyService.updateListAllStepRecom(listStepRecom);
  }

  private removeStepRecomAfterDelete() {
    let listStepRecom = this.notifyService.getListAllStepRecom();
    const index = listStepRecom.indexOf(this.stepRecom, 0);
    if (index > -1) {
      listStepRecom.splice(index, 1);
    }
    this.notifyService.updateListAllStepRecom(listStepRecom);

    // Select the current 'test step', then navigate to 'test-step-hub', i.e. to load and display the list of step recommendations after deletion.
    let testStepModel = this.notifyService.getSelectedTestStep();
    this.notifyService.updateTestStepSelection(testStepModel); //update the same test selection, so that adaption of display features in 'app.component.ts' can be triggered. 
    this.router.navigate(['test-step-hub']);
  }

  btnDeleteClicked() {
    if (!this.isReadonly) {
      let message = "The whole recommendation to test step will be deleted, sure to proceed?"
      const dialogRef = this._dialogConfirm.open(ConfirmDialogComponent, { data: message });
      dialogRef.afterClosed()
        .pipe(takeUntil(this.destroyed$))
        .subscribe(result => {
          if (result !== undefined) {
            if (dialogRef.componentInstance.confirmed) {
              this.deleteStepRecom();
            }
          };
        });
    }
  }


}
